import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';

import { Loading } from 'src/shared/components/loading/loading';

import {
  AddressTax,
  CollectingPlatform,
  FREQUENCY_ITEMS,
  PLATFORM_LABEL,
  TaxFieldsPayload,
  TaxFrequency,
  TaxesService,
  capitalize,
  dueRuleItems,
  dueRuleOptionValue,
} from 'src/shared/services/taxes.service';

@Component({
  templateUrl: 'add-edit-property-tax.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddEditPropertyTaxPage implements OnInit {

  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private client = inject(Client);
  private taxesService = inject(TaxesService);
  private demoBlock = inject(DemoBlockService);

  form = this.fb.nonNullable.group({
    property_id: [null as number | null, Validators.required],
    name: ['', Validators.required],
    category: [''],
    rate: ['', [Validators.min(0), Validators.max(100)]],
    agency: [''],
    frequency: ['' as TaxFrequency | ''],
    due_rule: [''],
    portal_url: [''],
    collecting_platforms: [[] as CollectingPlatform[]],
  });
  submitted = false;
  loaded = false;
  isRightSideContent = true;
  errorMessage = '';
  dialogParams: any;
  taxId: number | null = null;
  pageTitle = 'Add Tax';
  propertyItems: { value: number; viewValue: string }[] = [];
  categoryItems = [
    { value: 'occupancy', viewValue: 'Occupancy' },
    { value: 'sales', viewValue: 'Sales' },
    { value: 'tourism', viewValue: 'Tourism' },
    { value: 'license', viewValue: 'License' },
    { value: 'permit', viewValue: 'Permit' },
  ];
  frequencyItems = FREQUENCY_ITEMS;
  dueRuleItems = dueRuleItems();
  private originalDueRule = '';
  platformItems = (Object.keys(PLATFORM_LABEL) as CollectingPlatform[])
    .map(value => ({ value, viewValue: PLATFORM_LABEL[value] }));

  boundSave = this.save.bind(this);

  async ngOnInit() {
    try {
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.dialogParams = await this.storage.retrieve('dialog-params');
      const propertyId = this.navCtrl.getParam('propertyId') ?? this.dialogParams?.propertyId;
      this.taxId = this.navCtrl.getParam('taxId') ?? this.dialogParams?.taxId ?? null;
      this.pageTitle = this.taxId ? 'Edit Tax' : 'Add Tax';
      this.rightSidePanelService.setDialogPageTitle(this.pageTitle);

      if (this.taxId) {
        const tax = await this.taxesService.getTax(propertyId, this.taxId);
        if (!tax) {
          this.errorMessage = 'Tax not found';
          return;
        }
        this.patchForm(tax);
      } else {
        // The API refuses Add Tax without one: a tax with no frequency never gets filings.
        this.form.controls.frequency.addValidators(Validators.required);
        this.form.controls.frequency.updateValueAndValidity();
        await this.loadAddresses();
        if (propertyId) {
          this.form.patchValue({ property_id: propertyId });
        }
      }

      this.loaded = true;
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load page';
    }
  }

  async loadAddresses() {
    const addresses = await this.client.getAddresses();
    this.propertyItems = (addresses || []).map(address => ({
      value: address.id,
      viewValue: address.address_name || `${address.address1}${address.address2 ? ` ${address.address2}` : ''}, ${address.city}, ${address.zip}`,
    }));
  }

  // A tax's address can't change, so editing skips the property field. The
  // compliance report's categories are free text (e.g. "lodging"), so one outside
  // the list joins it rather than showing blank and being cleared on save.
  private patchForm(tax: AddressTax) {
    // The API can't clear a rate, so a blank one would save as "Tax saved." with the
    // old rate kept.
    if (tax.rate !== null && tax.rate !== undefined) {
      this.form.controls.rate.addValidators(Validators.required);
    }
    if (tax.category && !this.categoryItems.some(item => item.value === tax.category)) {
      this.categoryItems = [...this.categoryItems, { value: tax.category, viewValue: capitalize(tax.category) }];
    }
    // A report deadline the list doesn't have stays shown, and is only sent once changed.
    this.originalDueRule = tax.due_rule ? dueRuleOptionValue(tax.due_rule) : '';
    if (this.originalDueRule && !this.dueRuleItems.some(item => item.value === this.originalDueRule)) {
      this.dueRuleItems = [...this.dueRuleItems, { value: this.originalDueRule, viewValue: this.originalDueRule }];
    }
    this.form.patchValue({
      property_id: tax.address_id,
      name: tax.name,
      category: tax.category || '',
      rate: tax.rate !== null && tax.rate !== undefined ? String(tax.rate) : '',
      agency: tax.agency || '',
      frequency: (tax.frequency as TaxFrequency) || '',
      due_rule: this.originalDueRule,
      portal_url: tax.portal_url || '',
      collecting_platforms: tax.collecting_platforms || [],
    });
  }

  @Loading('', true)
  async save() {
    this.errorMessage = '';
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    // The demo interceptor answers blocked writes with a 200, so stop before the
    // request or the page reports a save that never happened.
    if (this.demoBlock.block()) {
      return;
    }
    try {
      const fields = this.buildFields();
      if (this.taxId) {
        await this.taxesService.updateTax(this.taxId, fields);
      } else {
        await this.taxesService.addTax({ ...fields, address_id: this.form.getRawValue().property_id, name: fields.name });
      }
      await this.storage.save('didMakePropertyTaxChanges', true);
      this.util.showSuccess(this.taxId ? 'Tax saved.' : 'Tax added successfully.');
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to save tax';
    }
  }

  // Editing sends the text fields even when blank so they can be cleared; the API
  // refuses a blank frequency or deadline, so they are left out, and a blank rate
  // only gets here for a tax that never had one.
  private buildFields(): TaxFieldsPayload {
    const val = this.form.getRawValue();
    const isEdit = !!this.taxId;
    const fields: TaxFieldsPayload = { name: val.name, collecting_platforms: val.collecting_platforms || [] };
    if (val.category || isEdit) { fields.category = val.category; }
    if (val.rate !== '' && val.rate !== null) { fields.rate = Number(val.rate); }
    if (val.agency || isEdit) { fields.agency = val.agency; }
    if (val.frequency) { fields.frequency = val.frequency; }
    if (val.due_rule && val.due_rule !== this.originalDueRule) { fields.due_rule = val.due_rule; }
    if (val.portal_url || isEdit) { fields.portal_url = val.portal_url; }
    return fields;
  }

  goBack() {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
